import { useContext, useEffect, useRef, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useHistory, useParams } from "react-router-dom";
import { NotificationContext } from "@solsys/solsys-reactjs";
import { useAuth } from "../contexts/AuthContext";
import {
  createIdempotencyKey,
  mutationInput,
} from "../utils/idempotency";
import { getOfflineService, getUnsyncedServices, subscribeOfflineServices } from "../utils/offlineStorage";
import { CRANE_EVENT_TYPE, TICKET_EVENT_TYPE } from "../eventTypes";
import TechnicianServiceInterface from "../components/service/TechnicianServiceInterface";
import TicketEvent from "../components/ticket/TicketEvent";
import CraneEvent from "../components/crane/CraneEvent";
import { useOnlineStatus } from "../components/service/PendingServiceNotice";

const TICKET_TYPES = [
  { id: 1, name: "Emergency" },
  { id: 2, name: "Repair" },
  { id: 3, name: "Maintenance" },
];

const craneQuery = gql`
  query phoneCranes($crane_list_request: crane_list_request) {
    crane(crane_list_request: $crane_list_request) {
      totalCount
      edges { id name }
    }
  }
`;

const assignedQuery = gql`
  query phoneAssignedDefinitions($request: serviceDefinition2Crane_list_request) {
    serviceDefinition2Crane(serviceDefinition2Crane_list_request: $request) {
      edges { service_definition_id }
    }
  }
`;

const definitionQuery = gql`
  query phoneDefinitions($request: service_definition_list_request) {
    service_definition(service_definition_list_request: $request) {
      edges { id name }
    }
  }
`;

const ticketQuery = gql`
  query phoneTickets($ticket_list_request: ticket_list_request) {
    ticket(ticket_list_request: $ticket_list_request) {
      totalCount
      edges { id name crane_id ticket_type_id }
    }
  }
`;

const ticketDetailQuery = gql`
  query phoneTicketDetail($ticket_list_request: ticket_list_request) {
    ticket(ticket_list_request: $ticket_list_request) {
      edges { id name description status_id ticket_type_id crane_id created_at }
    }
  }
`;

const locationQuery = gql`
  query phoneLocations($request: location_indicator_list_request) {
    location_indicator(location_indicator_list_request: $request) {
      edges { id name }
    }
  }
`;

const ticketMutation = gql`
  mutation phoneTicket($ticket_mutation_request: MutationInput) {
    ticket(ticket_mutation_request: $ticket_mutation_request) { id isNew }
  }
`;

const ticketEventMutation = gql`
  mutation phoneTicketEvent($ticket_event_mutation_request: MutationInput) {
    ticket_event(ticket_event_mutation_request: $ticket_event_mutation_request) { id }
  }
`;

const craneEventMutation = gql`
  mutation phoneCraneEvent($crane_event_mutation_request: MutationInput) {
    crane_event(crane_event_mutation_request: $crane_event_mutation_request) { id }
  }
`;

const subscriptionMutation = gql`
  mutation phoneTicketSub($ticket_subscription_mutation_request: MutationInput) {
    ticket_subscription(ticket_subscription_mutation_request: $ticket_subscription_mutation_request) { id }
  }
`;

const locationLinkMutation = gql`
  mutation phoneTicketLocation($ticket2location_indicator_mutation_request: MutationInput) {
    ticket2location_indicator(ticket2location_indicator_mutation_request: $ticket2location_indicator_mutation_request) { id }
  }
`;

function displayName(user) {
  return user?.name || user?.username || "there";
}

function greeting(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function moreThanShown(total, shown) {
  return typeof total === "number" && total > shown;
}

function useInfiniteSentinel(hasMore, shown, onMore) {
  const sentinelRef = useRef(null);
  const onMoreRef = useRef(onMore);
  onMoreRef.current = onMore;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;
    let pending = false;
    const observer = new IntersectionObserver((entries) => {
      if (pending || !entries.some((entry) => entry.isIntersecting)) return;
      pending = true;
      observer.disconnect();
      onMoreRef.current();
    }, { rootMargin: "280px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, shown]);

  return sentinelRef;
}

const RECENT_CRANES_KEY = "eperion.recentCranes";
const CRANE_PAGE = 50;
const TICKET_PAGE = 40;

function readRecentCranes() {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENT_CRANES_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((crane) => crane && crane.id != null && crane.name);
  } catch (error) {
    return [];
  }
}

function rememberCrane(crane) {
  if (crane?.id == null || !crane.name) return;
  const next = [
    { id: crane.id, name: crane.name },
    ...readRecentCranes().filter((item) => Number(item.id) !== Number(crane.id)),
  ].slice(0, 5);
  try {
    localStorage.setItem(RECENT_CRANES_KEY, JSON.stringify(next));
  } catch (error) {
    // The choice still applies for this visit.
  }
}

function useCraneName(craneId, hinted) {
  const { data } = useQuery(craneQuery, {
    variables: {
      crane_list_request: {
        filters: [{ field: "id", operator: "=", value: String(craneId) }],
        pagination: { limit: 1 },
      },
    },
    skip: !craneId || Boolean(hinted),
    fetchPolicy: "cache-first",
  });
  return hinted || data?.crane?.edges?.[0]?.name || (craneId ? `Crane ${craneId}` : "");
}

function openPhoneTicket(history, ticketId) {
  history.push(`/m/ticket/${ticketId}`);
}

export function TechHome() {
  const history = useHistory();
  const { user } = useAuth();
  const { addNotification } = useContext(NotificationContext);
  const online = useOnlineStatus();
  const [pending, setPending] = useState(() => getUnsyncedServices());
  const [sendingKey, setSendingKey] = useState(null);
  useEffect(() => subscribeOfflineServices(() => setPending(getUnsyncedServices())), []);
  useEffect(() => {
    const done = () => setSendingKey(null);
    window.addEventListener("ecrane-offline-services-sync-done", done);
    return () => window.removeEventListener("ecrane-offline-services-sync-done", done);
  }, []);
  const sendSaved = (key) => {
    if (!online) {
      addNotification("bad", "Still offline. This service stays on this phone until you are back online.");
      return;
    }
    setSendingKey(key);
    window.dispatchEvent(new CustomEvent("ecrane-offline-services-sync", { detail: { key } }));
  };
  const [ticketLimit, setTicketLimit] = useState(TICKET_PAGE);
  const { data: ticketData } = useQuery(ticketQuery, {
    variables: {
      ticket_list_request: {
        filters: [
          { field: "status_id", operator: "=", value: "1" },
          { field: "subscription_user_id", operator: "=", value: String(user?._id || "") },
        ],
        pagination: { limit: ticketLimit },
        sorting: [{ field: "id", direction: "DESC" }],
      },
    },
    skip: !user?._id,
    fetchPolicy: "network-only",
  });
  const tickets = ticketData?.ticket?.edges || [];
  const ticketTotal = ticketData?.ticket?.totalCount;
  useEffect(() => {
    if (typeof ticketTotal === "number" && tickets.length < ticketTotal && ticketLimit < ticketTotal) {
      setTicketLimit(ticketTotal);
    }
  }, [ticketTotal, tickets.length, ticketLimit]);
  const craneIds = [...new Set(tickets.map((ticket) => ticket.crane_id).filter(Boolean))];
  const { data: craneData } = useQuery(craneQuery, {
    variables: {
      crane_list_request: {
        filters: craneIds.length
          ? [{ field: "id", operator: "in", value: `(${craneIds.join(",")})` }]
          : [],
        pagination: { limit: Math.max(craneIds.length, 1) },
        sorting: [{ field: "name", direction: "ASC" }],
      },
    },
    skip: craneIds.length === 0,
    fetchPolicy: "network-only",
  });
  const cranes = craneData?.crane?.edges || [];
  return (
    <>
      <h1 className="phone-hello">
        <span>{ greeting() },</span>
        { displayName(user) }
      </h1>
      <p className="phone-muted">{ online ? "Online" : "Offline · a service you save stays on this phone" }</p>
      <div className="phone-actions">
        <button type="button" className="phone-primary" onClick={ () => history.push("/m/service") }>
          New Service
          <small>Inspection checklist</small>
        </button>
        <button type="button" className="phone-secondary" onClick={ () => history.push("/m/ticket") }>
          Report Problem
          <small>New ticket</small>
        </button>
      </div>
      { pending.map((item) => (
        <div className="phone-card phone-saved" key={ item.key }>
          <button
            type="button"
            className="phone-saved-open"
            onClick={ () => history.push(
              `/m/service/${item.crane_id}/${item.service_definition_id}?draft=${encodeURIComponent(item.key)}`,
              { craneName: item.crane_name || "" }
            ) }
          >
            <strong>{ item.blocked ? "Couldn't send" : "Not sent yet" }</strong>
            <div>{ item.service_definition_name || "Service" }{ item.crane_name ? ` · ${item.crane_name}` : "" }</div>
            <div className="phone-muted">
              { item.blocked
                ? item.blocked
                : (online ? "Tap Send, or open it to review." : "Sends when this phone is online.") }
            </div>
          </button>
          <button
            type="button"
            className="phone-saved-send"
            disabled={ !online || sendingKey === item.key }
            onClick={ () => sendSaved(item.key) }
          >
            { sendingKey === item.key ? "Sending…" : (item.blocked ? "Send again" : "Send") }
          </button>
        </div>
      )) }
      <div className="phone-section-title">
        My cranes
        <span>Open tickets</span>
      </div>
      { cranes.length === 0 && <p className="phone-muted">No open tickets assigned to you. Start a service to pick a crane.</p> }
      { cranes.map((crane) => {
        const mine = tickets.filter((ticket) => Number(ticket.crane_id) === Number(crane.id));
        const count = mine.length;
        const newest = mine[0];
        return (
          <button
            type="button"
            className="phone-row"
            key={ crane.id }
            onClick={ () => history.push(`/m/crane/${crane.id}`, { craneName: crane.name }) }
          >
            <span>
              <strong>{ crane.name }</strong>
              <em>{ newest?.name || "Open" }</em>
            </span>
            <span className="phone-badge" aria-label={ `${count} open` }>{ count }</span>
          </button>
        );
      }) }
    </>
  );
}

export function PickCrane({ destination = "service" }) {
  const history = useHistory();
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(CRANE_PAGE);
  const trimmed = search.trim();
  const filters = trimmed
    ? [{ field: "search", operator: "like", value: trimmed }]
    : [];
  const { data, loading } = useQuery(craneQuery, {
    variables: {
      crane_list_request: {
        filters,
        pagination: { limit },
        sorting: [{ field: "name", direction: "ASC" }],
      },
    },
    fetchPolicy: "network-only",
  });
  const cranes = data?.crane?.edges || [];
  const total = data?.crane?.totalCount;
  const recent = trimmed ? [] : readRecentCranes();
  const recentIds = new Set(recent.map((crane) => Number(crane.id)));
  const sentinelRef = useInfiniteSentinel(
    moreThanShown(total, cranes.length),
    cranes.length,
    () => setLimit((value) => value + CRANE_PAGE)
  );

  const choose = (crane) => {
    rememberCrane(crane);
    history.push(
      destination === "crane" ? `/m/crane/${crane.id}` : `/m/service/${crane.id}`,
      { craneName: crane.name }
    );
  };

  return (
    <>
      <h1 className="phone-hello">Which crane?</h1>
      <input
        className="phone-search"
        value={ search }
        placeholder="Search by name"
        onChange={ (event) => {
          setSearch(event.target.value);
          setLimit(CRANE_PAGE);
        } }
      />
      { loading && cranes.length === 0 && <p className="phone-muted">Loading cranes…</p> }
      { recent.length > 0 && <div className="phone-section-title">Recent</div> }
      { recent.map((crane) => (
        <button type="button" className="phone-row" key={ `recent-${crane.id}` } onClick={ () => choose(crane) }>
          <strong>{ crane.name }</strong>
        </button>
      )) }
      { cranes.filter((crane) => !recentIds.has(Number(crane.id))).map((crane) => (
        <button type="button" className="phone-row" key={ crane.id } onClick={ () => choose(crane) }>
          <strong>{ crane.name }</strong>
        </button>
      )) }
      { !loading && cranes.length === 0 && <p className="phone-muted">No cranes match that search.</p> }
      <div ref={ sentinelRef } className="phone-sentinel" />
    </>
  );
}

export function PickSection() {
  const history = useHistory();
  const { craneId } = useParams();
  const craneName = useCraneName(craneId, history.location.state?.craneName);
  const { data: assigned } = useQuery(assignedQuery, {
    variables: {
      request: {
        filters: [{ field: "crane_id", operator: "=", value: String(craneId) }],
        pagination: { limit: 100 },
      },
    },
    fetchPolicy: "network-only",
  });
  const ids = [...new Set((assigned?.serviceDefinition2Crane?.edges || [])
    .map((edge) => edge.service_definition_id)
    .filter((id) => id != null))];
  const { data: definitions, loading } = useQuery(definitionQuery, {
    variables: {
      request: {
        filters: [{ field: "id", operator: "in", value: `(${ids.join(",")})` }],
        pagination: { limit: 100 },
        sorting: [{ field: "name", direction: "ASC" }],
      },
    },
    skip: ids.length === 0,
    fetchPolicy: "network-only",
  });
  const sections = definitions?.service_definition?.edges || [];

  return (
    <>
      <h1 className="phone-hello">{ craneName }</h1>
      <p className="phone-muted">Pick the service.</p>
      { !loading && ids.length === 0 && (
        <>
          <p className="phone-muted">No service sections are assigned to this crane. You can still report a problem.</p>
          <button
            type="button"
            className="phone-secondary"
            onClick={ () => history.push(`/m/ticket?craneId=${craneId}`, { craneName }) }
          >
            Report Problem
          </button>
        </>
      ) }
      { sections.map((section) => (
        <button
          type="button"
          className="phone-row"
          key={ section.id }
          onClick={ () => history.push(`/m/service/${craneId}/${section.id}`, {
            craneName,
            definitionName: section.name,
          }) }
        >
          <strong>{ section.name }</strong>
        </button>
      )) }
    </>
  );
}

export function PhoneService() {
  const history = useHistory();
  const { craneId, definitionId } = useParams();
  const { craneName, definitionName } = history.location.state || {};
  const draftKey = new URLSearchParams(history.location.search).get("draft");
  const draft = draftKey ? getOfflineService(draftKey) : null;
  const rememberDraft = (storageKey) => {
    const params = new URLSearchParams(history.location.search);
    if (params.get("draft") === storageKey) return;
    params.set("draft", storageKey);
    history.replace({
      pathname: history.location.pathname,
      search: `?${params.toString()}`,
      state: history.location.state,
    });
  };

  return (
    <TechnicianServiceInterface
      action="add"
      navigate={ () => history.replace("/m") }
      options={ {
        crane_id: craneId,
        service_definition_id: definitionId,
        crane_name: craneName || draft?.crane_name || "",
        service_definition_name: definitionName || draft?.service_definition_name || "",
        embedded: true,
        draftKey: draft ? draftKey : undefined,
        draftBlocked: draft?.blocked || "",
        onKeptLocally: rememberDraft,
      } }
    />
  );
}

export function CraneOpenTickets() {
  const history = useHistory();
  const { craneId } = useParams();
  const craneName = useCraneName(craneId, history.location.state?.craneName);
  const openOnChat = new URLSearchParams(history.location.search).get("tab") === "Chat";
  const [view, setView] = useState(openOnChat ? "chat" : "tickets");
  const [limit, setLimit] = useState(TICKET_PAGE);
  const { data, loading } = useQuery(ticketQuery, {
    variables: {
      ticket_list_request: {
        filters: [
          { field: "crane_id", operator: "=", value: String(craneId) },
          { field: "status_id", operator: "=", value: "1" },
        ],
        pagination: { limit },
        sorting: [{ field: "id", direction: "DESC" }],
      },
    },
    fetchPolicy: "network-only",
  });
  const tickets = data?.ticket?.edges || [];
  const sentinelRef = useInfiniteSentinel(
    moreThanShown(data?.ticket?.totalCount, tickets.length),
    tickets.length,
    () => setLimit((value) => value + TICKET_PAGE)
  );

  return (
    <>
      <h1 className="phone-hello">{ craneName }</h1>
      <div className="phone-actions">
        <button
          type="button"
          className="phone-primary"
          onClick={ () => history.push(`/m/service/${craneId}`, { craneName }) }
        >
          Start service
        </button>
        <button
          type="button"
          className="phone-secondary"
          onClick={ () => history.push(`/m/ticket?craneId=${craneId}`, { craneName }) }
        >
          Report Problem
        </button>
      </div>
      <div className="phone-chips" role="tablist">
        <button
          type="button"
          className={ `phone-chip${view === "tickets" ? " is-selected" : ""}` }
          onClick={ () => setView("tickets") }
        >
          Open tickets
        </button>
        <button
          type="button"
          className={ `phone-chip${view === "chat" ? " is-selected" : ""}` }
          onClick={ () => setView("chat") }
        >
          Chat
        </button>
      </div>
      { view === "chat" ? (
        <div className="phone-chat">
          <CraneEvent phone crane_id={ Number(craneId) } />
        </div>
      ) : (
        <>
          { loading && tickets.length === 0 && <p className="phone-muted">Loading tickets…</p> }
          { !loading && tickets.length === 0 && <p className="phone-muted">No open tickets on this crane.</p> }
          { tickets.map((ticket) => (
            <button
              type="button"
              className="phone-row"
              key={ ticket.id }
              onClick={ () => openPhoneTicket(history, ticket.id) }
            >
              <span>
                <strong>{ ticket.name || `Ticket ${ticket.id}` }</strong>
                <em>#{ ticket.id }</em>
              </span>
            </button>
          )) }
          <div ref={ sentinelRef } className="phone-sentinel" />
        </>
      ) }
    </>
  );
}

export function QuickTicket() {
  const history = useHistory();
  const { user } = useAuth();
  const { addNotification } = useContext(NotificationContext);
  const params = new URLSearchParams(history.location.search);
  const initialCrane = params.get("craneId") || "";
  const [craneId, setCraneId] = useState(initialCrane);
  const [craneName, setCraneName] = useState(history.location.state?.craneName || "");
  const [pickingCrane, setPickingCrane] = useState(!initialCrane);
  const [search, setSearch] = useState("");
  const [craneLimit, setCraneLimit] = useState(CRANE_PAGE);
  const [ticketType, setTicketType] = useState(2);
  const [locationId, setLocationId] = useState(null);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [sentId, setSentId] = useState(null);
  const keyRef = useRef(createIdempotencyKey());
  const [saveTicket] = useMutation(ticketMutation);
  const [saveEvent] = useMutation(ticketEventMutation);
  const [saveCraneEvent] = useMutation(craneEventMutation);
  const [saveSubscription] = useMutation(subscriptionMutation);
  const [saveLocation] = useMutation(locationLinkMutation);

  const filters = search.trim()
    ? [{ field: "search", operator: "like", value: search.trim() }]
    : [];
  const { data: craneData } = useQuery(craneQuery, {
    variables: {
      crane_list_request: {
        filters,
        pagination: { limit: craneLimit },
        sorting: [{ field: "name", direction: "ASC" }],
      },
    },
    skip: !pickingCrane,
    fetchPolicy: "network-only",
  });
  const pickerCranes = craneData?.crane?.edges || [];
  const craneSentinelRef = useInfiniteSentinel(
    pickingCrane && moreThanShown(craneData?.crane?.totalCount, pickerCranes.length),
    pickerCranes.length,
    () => setCraneLimit((value) => value + CRANE_PAGE)
  );
  const { data: locationData } = useQuery(locationQuery, {
    variables: {
      request: {
        pagination: { limit: 8 },
        sorting: [{ field: "name", direction: "ASC" }],
      },
    },
    fetchPolicy: "network-only",
  });
  const locations = locationData?.location_indicator?.edges || [];
  const location = locations.find((item) => Number(item.id) === Number(locationId));
  const type = TICKET_TYPES.find((item) => item.id === ticketType);

  const send = async () => {
    if (!craneId || !description.trim() || saving) return;
    setSaving(true);
    const name = [type?.name || "Ticket", location?.name, craneName].filter(Boolean).join(" – ").slice(0, 120);
    const who = displayName(user);
    try {
      const saved = await saveTicket({
        variables: {
          ticket_mutation_request: mutationInput({
            properties: [
              { key: "name", string: name },
              { key: "description", string: description.trim() },
              { key: "ticket_type_id", number: ticketType },
              { key: "status_id", number: 1 },
              { key: "crane_id", number: Number(craneId) },
            ],
            idempotencyKey: keyRef.current,
          }),
        },
      });
      const id = saved.data?.ticket?.id;
      if (!id) throw new Error("Ticket was not created");
      if (locationId) {
        await saveLocation({
          variables: {
            ticket2location_indicator_mutation_request: mutationInput({
              properties: [
                { key: "ticket_id", number: Number(id) },
                { key: "location_indicator_id", number: Number(locationId) },
              ],
              idempotencyKey: `${keyRef.current}-loc`,
            }),
          },
        });
      }
      if (user?._id) {
        await saveSubscription({
          variables: {
            ticket_subscription_mutation_request: mutationInput({
              properties: [
                { key: "ticket_id", number: Number(id) },
                { key: "user_id", string: String(user._id) },
              ],
              idempotencyKey: `${keyRef.current}-sub`,
            }),
          },
        });
      }
      const message = `New ${type?.name || "ticket"} ticket #${id} "${name}" opened by ${who}`;
      await saveEvent({
        variables: {
          ticket_event_mutation_request: mutationInput({
            properties: [
              { key: "type_id", number: TICKET_EVENT_TYPE.OPEN },
              { key: "message", string: message },
              { key: "ticket_id", number: Number(id) },
              { key: "user_id", string: String(user?._id || "") },
              { key: "user_display_name", string: who },
              { key: "url", string: `/m/ticket/${id}` },
            ],
            idempotencyKey: `${keyRef.current}-event`,
          }),
        },
      });
      await saveCraneEvent({
        variables: {
          crane_event_mutation_request: mutationInput({
            properties: [
              { key: "type_id", number: CRANE_EVENT_TYPE.TICKET_OPENED },
              { key: "message", string: message },
              { key: "crane_id", number: Number(craneId) },
              { key: "user_id", string: String(user?._id || "") },
              { key: "user_display_name", string: who },
              { key: "url", string: `/m/crane/${craneId}` },
            ],
            idempotencyKey: `${keyRef.current}-crane`,
          }),
        },
      });
      addNotification("good", `Ticket #${id} sent`);
      setSentId(id);
      setSaving(false);
    } catch (error) {
      console.error(error);
      addNotification("bad", "Could not send the ticket");
      setSaving(false);
    }
  };

  if (sentId) {
    return (
      <>
        <h1 className="phone-hello">Ticket #{ sentId }</h1>
        <p className="phone-muted">Sent. The office has this problem.</p>
        <div className="phone-actions">
          <button type="button" className="phone-primary" onClick={ () => openPhoneTicket(history, sentId) }>
            View ticket
          </button>
          <button type="button" className="phone-secondary" onClick={ () => history.push("/m") }>
            Back to today
          </button>
        </div>
      </>
    );
  }

  if (pickingCrane) {
    return (
      <>
        <h1 className="phone-hello">Which crane?</h1>
        <input
          className="phone-search"
          value={ search }
          placeholder="Search by name"
          onChange={ (event) => {
            setSearch(event.target.value);
            setCraneLimit(CRANE_PAGE);
          } }
        />
        { pickerCranes.map((crane) => (
          <button
            type="button"
            className="phone-row"
            key={ crane.id }
            onClick={ () => {
              setCraneId(String(crane.id));
              setCraneName(crane.name);
              setPickingCrane(false);
            } }
          >
            <strong>{ crane.name }</strong>
          </button>
        )) }
        <div ref={ craneSentinelRef } className="phone-sentinel" />
      </>
    );
  }

  return (
    <>
      <h1 className="phone-hello">Report a problem</h1>
      <button type="button" className="phone-row" onClick={ () => setPickingCrane(true) }>
        <span>
          <strong>{ craneName || `Crane ${craneId}` }</strong>
          <em>Change crane</em>
        </span>
      </button>
      <div className="phone-section-title">What kind of problem?</div>
      <div className="phone-choices">
        { TICKET_TYPES.map((item) => (
          <button
            type="button"
            key={ item.id }
            className={ `phone-choice${ticketType === item.id ? " is-selected" : ""}` }
            onClick={ () => setTicketType(item.id) }
          >
            { item.name }
          </button>
        )) }
      </div>
      <div className="phone-section-title">Where on the crane?</div>
      <div className="phone-chips">
        { locations.map((item) => (
          <button
            type="button"
            key={ item.id }
            className={ `phone-chip${Number(locationId) === Number(item.id) ? " is-selected" : ""}` }
            onClick={ () => setLocationId(Number(locationId) === Number(item.id) ? null : item.id) }
          >
            { item.name }
          </button>
        )) }
      </div>
      <div className="phone-section-title">What's wrong?</div>
      <textarea
        className="phone-note"
        value={ description }
        onChange={ (event) => setDescription(event.target.value) }
      />
      <p className="phone-muted">The ticket name and open status are filled in. The office can add the rest.</p>
      <button type="button" className="phone-send" disabled={ saving || !description.trim() } onClick={ send }>
        { saving ? "Sending…" : "Send ticket" }
      </button>
    </>
  );
}

export function PhoneTicket() {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const { addNotification } = useContext(NotificationContext);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeComment, setCloseComment] = useState("");
  const [closing, setClosing] = useState(false);
  const [saveTicket] = useMutation(ticketMutation);
  const [saveEvent] = useMutation(ticketEventMutation);
  const [saveCraneEvent] = useMutation(craneEventMutation);
  const { data, loading, refetch } = useQuery(ticketDetailQuery, {
    variables: {
      ticket_list_request: {
        filters: [{ field: "id", operator: "=", value: String(ticketId) }],
        pagination: { limit: 1 },
      },
    },
    skip: !ticketId,
    fetchPolicy: "network-only",
  });
  const ticket = data?.ticket?.edges?.[0];
  const craneName = useCraneName(ticket?.crane_id, "");
  const type = TICKET_TYPES.find((item) => item.id === Number(ticket?.ticket_type_id));
  const status = Number(ticket?.status_id) === 2 ? "Closed" : "Open";
  const created = ticket?.created_at ? new Date(ticket.created_at) : null;
  const createdLabel = created && !Number.isNaN(created.getTime())
    ? created.toLocaleString()
    : "";
  const who = displayName(user);

  const closeTicket = async () => {
    if (!ticket || closing || Number(ticket.status_id) === 2) return;
    setClosing(true);
    const comment = closeComment.trim();
    const typeName = type?.name || "Ticket";
    const message = comment
      ? `Ticket #${ticket.id} (${typeName}) Closed by ${who} for Crane #${ticket.crane_id}. Comment: ${comment}`
      : `Ticket #${ticket.id} (${typeName}) Closed by ${who} for Crane #${ticket.crane_id}`;
    try {
      await saveTicket({
        variables: {
          ticket_mutation_request: mutationInput({
            id: Number(ticket.id),
            properties: [{ key: "status_id", number: 2 }],
          }),
        },
      });
      await saveEvent({
        variables: {
          ticket_event_mutation_request: mutationInput({
            properties: [
              { key: "type_id", number: TICKET_EVENT_TYPE.CLOSED },
              { key: "message", string: message },
              { key: "ticket_id", number: Number(ticket.id) },
              { key: "user_id", string: String(user?._id || "") },
              { key: "user_display_name", string: who },
              { key: "url", string: `/m/ticket/${ticket.id}` },
            ],
            idempotencyKey: createIdempotencyKey(),
          }),
        },
      });
      if (ticket.crane_id != null) {
        await saveCraneEvent({
          variables: {
            crane_event_mutation_request: mutationInput({
              properties: [
                { key: "type_id", number: CRANE_EVENT_TYPE.TICKET_CLOSED },
                { key: "message", string: `Ticket #${ticket.id} "${ticket.name || ""}" (${typeName}) Closed by ${who}` },
                { key: "crane_id", number: Number(ticket.crane_id) },
                { key: "user_id", string: String(user?._id || "") },
                { key: "user_display_name", string: who },
                { key: "url", string: `/m/crane/${ticket.crane_id}?tab=Chat` },
              ],
              idempotencyKey: createIdempotencyKey(),
            }),
          },
        });
      }
      setCloseOpen(false);
      setCloseComment("");
      await refetch();
      addNotification("good", `Ticket #${ticket.id} closed`);
    } catch (error) {
      console.error(error);
      addNotification("bad", "Failed to close ticket");
    } finally {
      setClosing(false);
    }
  };

  return (
    <>
      <h1 className="phone-hello">{ ticket?.name || (loading ? "Ticket" : `Ticket ${ticketId}`) }</h1>
      { loading && <p className="phone-muted">Loading ticket…</p> }
      { !loading && !ticket && <p className="phone-muted">This ticket is not available.</p> }
      { ticket && (
        <>
          <p className="phone-muted">#{ ticket.id } · { status }{ type ? ` · ${type.name}` : "" }</p>
          { craneName && <p className="phone-muted">{ craneName }</p> }
          { createdLabel && <p className="phone-muted">{ createdLabel }</p> }
          <div className="phone-section-title">What's wrong</div>
          <p className="phone-pre">{ ticket.description || "No description." }</p>
          { Number(ticket.status_id) !== 2 && (
            <button type="button" className="phone-close-ticket" onClick={ () => setCloseOpen(true) }>
              Close ticket
            </button>
          ) }
          <div className="phone-chat">
            <TicketEvent
              phone
              ticket_id={ Number(ticketId) }
              crane_id={ ticket.crane_id != null ? Number(ticket.crane_id) : undefined }
            />
          </div>
          { closeOpen && (
            <div className="phone-dialog-backdrop">
              <div className="phone-dialog" role="dialog" aria-labelledby="phone-close-title">
                <h2 id="phone-close-title">Close ticket</h2>
                <textarea
                  className="phone-note"
                  value={ closeComment }
                  placeholder="Comment (optional)"
                  onChange={ (event) => setCloseComment(event.target.value) }
                />
                <div className="phone-dialog-actions">
                  <button type="button" className="phone-secondary" onClick={ () => setCloseOpen(false) } disabled={ closing }>
                    Cancel
                  </button>
                  <button type="button" className="phone-send" onClick={ closeTicket } disabled={ closing }>
                    { closing ? "Closing…" : "Close" }
                  </button>
                </div>
              </div>
            </div>
          ) }
        </>
      ) }
    </>
  );
}

export function MyTickets() {
  const history = useHistory();
  const { user } = useAuth();
  const [limit, setLimit] = useState(TICKET_PAGE);
  const { data, loading } = useQuery(ticketQuery, {
    variables: {
      ticket_list_request: {
        filters: [
          { field: "status_id", operator: "=", value: "1" },
          { field: "subscription_user_id", operator: "=", value: String(user?._id || "") },
        ],
        pagination: { limit },
        sorting: [{ field: "id", direction: "DESC" }],
      },
    },
    skip: !user?._id,
    fetchPolicy: "network-only",
  });
  const tickets = data?.ticket?.edges || [];
  const sentinelRef = useInfiniteSentinel(
    moreThanShown(data?.ticket?.totalCount, tickets.length),
    tickets.length,
    () => setLimit((value) => value + TICKET_PAGE)
  );

  return (
    <>
      <h1 className="phone-hello">My tickets</h1>
      { loading && tickets.length === 0 && <p className="phone-muted">Loading tickets…</p> }
      { !loading && tickets.length === 0 && <p className="phone-muted">No open tickets assigned to you.</p> }
      { tickets.map((ticket) => (
        <button
          type="button"
          className="phone-row"
          key={ ticket.id }
          onClick={ () => openPhoneTicket(history, ticket.id) }
        >
          <span>
            <strong>{ ticket.name || `Ticket ${ticket.id}` }</strong>
            <em>#{ ticket.id }</em>
          </span>
        </button>
      )) }
      <div ref={ sentinelRef } className="phone-sentinel" />
    </>
  );
}
